import {ChangeDetectionStrategy, ChangeDetectorRef, Component, input} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";
import {NgxColorsModule} from "ngx-colors";
import {TranslateModule} from "@ngx-translate/core";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatOption, MatSelect, MatSelectTrigger} from "@angular/material/select";
import {MatTooltip} from "@angular/material/tooltip";
import {Celleramme} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/TabellcelleRamme";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";

export type SkjemaType = ReturnType<typeof RiktekstRedigeringTabellRammevalgKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];
export type SkjemaVerdierRammer = NonNullable<SkjemaVerdier['rammer']>;

const debug = initDebugger('RiktekstRedigeringTabellRammevalgKomponent');

/**
 * Gui for å redigere tabellcellerammer
 */
@Component({
    selector: 'riktekst-redigering-tabell-rammevalg',
    templateUrl: 'tabell-rammevalg.html',
    styleUrl: 'tabell-rammevalg.scss',
    host: {
        //for å unngå at meny som komponenten benyttes i lukkes når bruker klikker i skjemaet
        '(click)': '$event.stopPropagation()'
    },
    imports: [
        MatIcon,
        MatButton,
        NgxColorsModule,
        TranslateModule,
        ReactiveFormsModule,
        MatOption,
        MatSelect,
        MatSelectTrigger,
        MatTooltip,

    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RiktekstRedigeringTabellRammevalgKomponent {

    static opprettSkjema() {
        let skjema = new FormGroup({
            rammer: new FormGroup({
                alle: new FormControl(false, {nonNullable: true}),
                mellom: new FormControl(false, {nonNullable: true}),
                omkring: new FormControl(false, {nonNullable: true}),
                vertikalMellom: new FormControl(false, {nonNullable: true}),
                horisontalMellom: new FormControl(false, {nonNullable: true}),
                venstre: new FormControl(false, {nonNullable: true}),
                topp: new FormControl(false, {nonNullable: true}),
                hoeyre: new FormControl(false, {nonNullable: true}),
                bunn: new FormControl(false, {nonNullable: true}),
            }),
            farge: new FormControl('#999', {nonNullable: true}),
            tykkelse: new FormControl(1, {nonNullable: true})
        });
        skjema.patchValue(blankeSkjemaverdier());
        return skjema;
    }

    /** Sann dersom ingen rammer er satt i skjemaet. */
    static erIngenRammerSatt(skjema: SkjemaVerdier): boolean {
        return ingenRammerSatt(skjema.rammer!);
    }

    static somCelleramme(skjema: SkjemaVerdier): Celleramme {
        return {
            venstre: skjema.rammer!.venstre!,
            topp: skjema.rammer!.topp!,
            høyre: skjema.rammer!.hoeyre!,
            bunn: skjema.rammer!.bunn!,
            farge: skjema.farge!,
            tykkelse: skjema.tykkelse!
        };
    }

    static fraCelleramme(rammevalg: Celleramme | null): SkjemaVerdier {
        if (erNil(rammevalg)) return blankeSkjemaverdier();
        return {
            rammer: {
                alle: false,
                mellom: false,
                omkring: false,
                vertikalMellom: false,
                horisontalMellom: false,
                venstre: rammevalg.venstre,
                topp: rammevalg.topp,
                hoeyre: rammevalg.høyre,
                bunn: rammevalg.bunn!,
            },
            farge: rammevalg.farge,
            tykkelse: rammevalg.tykkelse
        };
    }

    skjema = input.required<SkjemaType>();

    tilgjengeligeTykkelse = [1, 2, 3, 4, 5];

    constructor(private cdr: ChangeDetectorRef) {
    }

    /** Sørger for at endringer i GUI blir tegnet ut.
     * Siden skjema får sine verdier oppdatert med '{emitEvent: false}' kan vi ikke lytte etter endringer,
     * men må belage oss på at denne metoden kalles etter at skjemaet har blitt oppdatert. */
    oppdaterGUI() {
        this.cdr.markForCheck();
    }

    toggleRammevalgSettAlleAndreFalse(feltnavn: string) {
        let rammer = rammerMedAlleSattTilFalse();
        toggleRammeValg(rammer, feltnavn);
        this.skjema().controls.rammer.patchValue(rammer);
    }

    toggleRammevalgSettGrupperteFalse(feltnavn: string) {
        let rammer = this.skjema().value.rammer!
        toggleRammeValg(rammer, feltnavn);
        settGrupperteFalse(rammer);
        this.skjema().controls.rammer.patchValue(rammer);
    }

    fjernRammer() {
        this.skjema().controls.rammer.patchValue(rammerMedAlleSattTilFalse());
    }

    //private hjelpemetoder

}

function blankeSkjemaverdier(): SkjemaVerdier {
    return {
        rammer: rammerMedAlleSattTilFalse(),
        farge: '#999',
        tykkelse: 1
    }
}

function rammerMedAlleSattTilFalse(): SkjemaVerdierRammer {
    return {
        alle: false,
        mellom: false,
        omkring: false,
        vertikalMellom: false,
        horisontalMellom: false,
        topp: false,
        hoeyre: false,
        bunn: false,
        venstre: false
    }
}

function toggleRammeValg(rammer: SkjemaVerdierRammer, feltnavn: string) {
    // @ts-ignore
    rammer[feltnavn] = !rammer[feltnavn];
}

function settGrupperteFalse(rammer: SkjemaVerdierRammer) {
    rammer.alle = false;
    rammer.mellom = false;
    rammer.omkring = false;
}

function ingenRammerSatt(rammer: SkjemaVerdierRammer) {
    return Object.values(rammer).every(valg => valg === false);
}
